import type { AxiosError, AxiosResponse } from 'axios';
import HttpStatus from '~/enums/HttpStatus.enum';
import type { MySsException } from '~/types/MySsException';

/**
 * This function is responsible for treating AxiosErrors and returning a MySsException object.
 * @param error {AxiosError}
 * @returns mySsException {MySsException}
 */
export function ExceptionHandler(error?: AxiosError): MySsException {
  return { ...adaptErrorInfo(error) };
}

/**
 * This function validate if a deserialized object is MySsException
 * @param jsonString {string}
 * @returns jsonString is MySsException {boolean}
 */
// @ts-expect-error - This function validate if a deserialized object is MySsException
export function isValidMySsException(jsonString: string): jsonString is MySsException {
  try {
    const obj = JSON.parse(jsonString);
    return isMySsException(obj);
  } catch {
    return false;
  }
}

/**
 * This function validates if the object is MySsException.
 * @param obj {any}
 * @returns obj is MySsException {boolean}
 */
export function isMySsException(obj: any): obj is MySsException {
  return (
    typeof obj.severity === 'string' &&
    Array.isArray(obj.messages) &&
    obj.messages.every((message: any) => typeof message === 'string') &&
    typeof obj.status === 'number'
  );
}

/**
 * This function is responsible for treating the error and return a MySsException object.
 * @param  error {any}
 * @returns mySsException {MySsException}
 */
export function errorTreatment(error: any): MySsException {
  let mySsException: MySsException = mountMySsUnhandledException();

  if (!(error instanceof Error)) {
    if (isMySsException(error)) mySsException = { ...mySsException, ...error };
  } else if (isValidMySsException(error.message)) {
    mySsException = { ...mySsException, ...JSON.parse(error.message) };
  } else {
    mySsException = { ...mySsException, severity: 'error', messages: [error.message] };
  }

  return mySsException;
}

function adaptErrorInfo(error?: AxiosError): MySsException {
  if (isNullOrEmptyObject(error)) return mountMySsUnhandledException();
  const { code, response } = error!;
  const statusCode = response?.status ?? Number(code);
  switch (statusCode) {
    case HttpStatus.NOT_FOUND:
      return mountMySsException(
        response ?? mountAxiosResponse(statusCode, ERRORS.STATUS.NOT_FOUND),
      );
    case HttpStatus.INTERNAL_SERVER_ERROR:
      return mountMySsException(
        mountAxiosResponse(statusCode, ERRORS.STATUS.INTERNAL_SERVER_ERROR),
      );
    case HttpStatus.SERVICE_UNAVAILABLE:
      return mountMySsException(mountAxiosResponse(statusCode, ERRORS.STATUS.SERVICE_UNAVAILABLE));
    case HttpStatus.BAD_GATEWAY:
      return mountMySsException(mountAxiosResponse(statusCode, ERRORS.STATUS.BAD_GATEWAY));
    case HttpStatus.UNPROCESSABLE_ENTITY:
      return response ? mountMySsException(response) : mountMySsUnhandledException();
    default:
      return mountMySsUnhandledException();
  }
}

function mountMySsUnhandledException(): MySsException {
  return {
    messages: [generateErrorKey()],
    status: 422,
    severity: 'warn',
  };
}

function mountMySsException({ status, data }: Partial<AxiosResponse>): MySsException {
  if (status === 404) {
    if (data.isNotFound) {
      return mountNotFoundExceptionWhenResponseIsEmpty();
    }
    return mountNotFoundException();
  }

  const { errors } = data;
  const messages = errors.map(({ key }: MySsApiError) => generateErrorKey(key));
  const args = data.errors.map((item: any) => (item.args ? item.args : null));
  return {
    messages,
    status: status!,
    severity: 'error',
    args: args,
  };
}

function mountNotFoundException(): MySsException {
  return {
    messages: ['EXCEPTIONS.NOT_FOUND'],
    status: 404,
    severity: 'info',
  } as MySsException;
}

function mountNotFoundExceptionWhenResponseIsEmpty(): MySsException {
  return {
    messages: [''],
    status: 404,
    severity: 'info',
  } as MySsException;
}

function mountAxiosResponse(status: number, key: string): Partial<AxiosResponse> {
  const axiosResponse = {} as AxiosResponse;

  axiosResponse.data = { errors: [{ key }] };
  axiosResponse.status = status;

  return axiosResponse;
}

function generateErrorKey(key?: string): string {
  return !key ? `EXCEPTIONS.UNEXPECTED` : `EXCEPTIONS.${key.replace(/_/g, '.')}`;
}
